<template>
  <div class="card summary-card">
    <div class="card-body">
      <!-- 月選択 -->
      <div class="summary-header">
        <div class="month-selector">
          <div class="year-selector">
            <button class="year-nav-btn" @click="changeYear(-1)"><i class="fas fa-chevron-left"></i></button>
            <span class="year-label">{{ currentDate.getFullYear() }}年</span>
            <button class="year-nav-btn" @click="changeYear(1)"><i class="fas fa-chevron-right"></i></button>
          </div>
          <div class="month-btn-group">
            <button
              v-for="m in 12"
              :key="m"
              :class="['month-btn', { active: currentDate.getMonth() === m - 1 }]"
              @click="selectMonth(m - 1)"
            >{{ m }}月</button>
          </div>
        </div>
        <div class="header-actions">
          <label class="archive-toggle">
            <input type="checkbox" v-model="showArchived" />
            <span>アーカイブ済みを表示</span>
          </label>
          <button class="btn btn-success" :disabled="loading || loadError || shops.length === 0" @click="excelDownload">
            <i class="fas fa-file-excel mr-1"></i>Excel出力
          </button>
        </div>
      </div>

      <div v-if="loading" class="text-muted py-4">読み込み中...</div>

      <div v-else-if="loadError" class="text-danger py-4">集計の取得に失敗しました。時間をおいて再度お試しください。</div>

      <template v-else>
        <!-- 会社合計 -->
        <div class="company-total">
          <div class="company-total-grid">
            <div class="total-item"><span class="total-label">店舗数</span><span class="total-value">{{ company.shopCount }}店</span></div>
            <div class="total-item">
              <span class="total-label">スタッフ数</span>
              <span class="total-value">{{ company.staffCount }}名</span>
              <span v-if="(company.archivedStaffCount || 0) > 0" class="total-sub">
                うちアーカイブ済み {{ company.archivedStaffCount }}名
              </span>
            </div>
            <div class="total-item"><span class="total-label">勤務時間</span><span class="total-value">{{ toHhmm(company.workMinutes) }}</span></div>
            <div class="total-item"><span class="total-label">8時間超</span><span class="total-value">{{ toHhmm(company.beyond8Minutes) }}</span></div>
            <div class="total-item"><span class="total-label">深夜</span><span class="total-value">{{ toHhmm(company.lateNightMinutes) }}</span></div>
            <div class="total-item"><span class="total-label">交通費合計</span><span class="total-value">{{ yen(company.transportationTotal) }}</span></div>
            <div class="total-item total-item-emphasis"><span class="total-label">支給合計</span><span class="total-value">{{ yen(company.payrollTotal) }}</span></div>
          </div>
          <p class="total-note">※支給額はシフトパターン店舗 {{ company.payrollShopCount }}件 の分のみ</p>
          <p class="total-note">※兼務スタッフは各店舗に計上、会社合計は実人数</p>
        </div>

        <div v-if="shops.length === 0" class="text-muted py-4">対象の店舗がありません。</div>

        <template v-else>
          <!-- タブ（Bootstrap の nav-tabs は本リポジトリに前例が無いため、
               月ボタンと同じ独自 CSS のボタン切替にしている） -->
          <div class="summary-tabs">
            <button
              :class="['summary-tab', { active: activeTab === 'shop' }]"
              @click="activeTab = 'shop'"
            >店舗別</button>
            <button
              :class="['summary-tab', { active: activeTab === 'staff' }]"
              @click="activeTab = 'staff'"
            >スタッフ別</button>
          </div>

          <!-- 店舗 / スタッフ -->
          <div v-if="activeTab === 'shop'" class="summary-table-wrap">
            <table class="table summary-table">
              <thead>
                <tr>
                  <th class="th-name">店舗 / スタッフ</th>
                  <th>人数</th>
                  <th>出勤</th>
                  <th>勤務時間</th>
                  <th>8時間超</th>
                  <th>深夜</th>
                  <th>時給</th>
                  <th>基本給</th>
                  <th>8時間超支給</th>
                  <th>深夜支給</th>
                  <th>交通費</th>
                  <th>支給合計</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="shop in shops" :key="shop.shopId">
                  <tr class="row-shop" @click="toggleShop(shop.shopId)">
                    <td class="td-name">
                      <i :class="expanded[shop.shopId] ? 'fas fa-caret-down' : 'fas fa-caret-right'"></i>
                      {{ shop.shopName }}
                      <span :class="shop.shiftPatternType === 2 ? 'badge-shift' : 'badge-corporate'">
                        {{ shop.shiftPatternType === 2 ? 'シフト制' : '企業パターン' }}
                      </span>
                    </td>
                    <td>
                      {{ shop.staffCount }}名
                      <span v-if="(shop.archivedStaffCount || 0) > 0" class="cell-sub">
                        （うち{{ shop.archivedStaffCount }}）
                      </span>
                    </td>
                    <td>{{ shop.workDays }}日</td>
                    <td>{{ toHhmm(shop.workMinutes) }}</td>
                    <td>{{ toHhmm(shop.beyond8Minutes) }}</td>
                    <td>{{ toHhmm(shop.lateNightMinutes) }}</td>
                    <td></td>
                    <td>{{ yen(shop.basePay) }}</td>
                    <td>{{ yen(shop.beyond8Pay) }}</td>
                    <td>{{ yen(shop.lateNightPay) }}</td>
                    <td>{{ yen(shop.transportationTotal) }}</td>
                    <td class="td-emphasis">{{ yen(shop.payrollTotal) }}</td>
                  </tr>
                  <tr v-for="staff in (expanded[shop.shopId] ? shop.staff : [])" :key="shop.shopId + '-' + staff.staffId" class="row-staff">
                    <td class="td-name td-staff-name">
                      {{ staff.name }}
                      <span v-if="staff.isArchived" class="badge-archived">アーカイブ済み</span>
                    </td>
                    <td></td>
                    <td>{{ staff.workDays }}日</td>
                    <td>{{ toHhmm(staff.workMinutes) }}</td>
                    <td>{{ toHhmm(staff.beyond8Minutes) }}</td>
                    <td>{{ toHhmm(staff.lateNightMinutes) }}</td>
                    <td>{{ yen(staff.hourlyWage) }}</td>
                    <td>{{ yen(staff.basePay) }}</td>
                    <td>{{ yen(staff.beyond8Pay) }}</td>
                    <td>{{ yen(staff.lateNightPay) }}</td>
                    <td>{{ yen(staff.transportationTotal) }}</td>
                    <td>{{ yen(staff.payrollTotal) }}</td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <!-- スタッフ / 店舗 -->
          <template v-else>
            <div v-if="staffList.length === 0" class="text-muted py-4">対象のスタッフがいません。</div>
            <template v-else>
              <div class="summary-table-wrap">
                <table class="table summary-table">
                  <thead>
                    <tr>
                      <th class="th-name">スタッフ / 店舗</th>
                      <th>店舗数</th>
                      <th>出勤</th>
                      <th>勤務時間</th>
                      <th>8時間超</th>
                      <th>深夜</th>
                      <th>時給</th>
                      <th>基本給</th>
                      <th>8時間超支給</th>
                      <th>深夜支給</th>
                      <th>交通費</th>
                      <th>支給合計</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="staff in staffList" :key="staff.staffId">
                      <tr class="row-staff-total" @click="toggleStaff(staff.staffId)">
                        <td class="td-name">
                          <i :class="expandedStaff[staff.staffId] ? 'fas fa-caret-down' : 'fas fa-caret-right'"></i>
                          {{ staff.name }}
                          <span v-if="staff.isArchived" class="badge-archived">アーカイブ済み</span>
                        </td>
                        <td>{{ staff.shopCount }}店</td>
                        <td>{{ staff.workDays }}日</td>
                        <td>{{ toHhmm(staff.workMinutes) }}</td>
                        <td>{{ toHhmm(staff.beyond8Minutes) }}</td>
                        <td>{{ toHhmm(staff.lateNightMinutes) }}</td>
                        <td>{{ yen(staff.hourlyWage) }}</td>
                        <td>{{ yen(staff.basePay) }}</td>
                        <td>{{ yen(staff.beyond8Pay) }}</td>
                        <td>{{ yen(staff.lateNightPay) }}</td>
                        <td>{{ yen(staff.transportationTotal) }}</td>
                        <td class="td-emphasis">{{ yen(staff.payrollTotal) }}</td>
                      </tr>
                      <tr
                        v-for="shop in (expandedStaff[staff.staffId] ? staff.shops : [])"
                        :key="staff.staffId + '-' + shop.shopId"
                        class="row-breakdown"
                      >
                        <td class="td-name td-breakdown-name">
                          {{ shop.shopName }}
                          <span :class="shop.shiftPatternType === 2 ? 'badge-shift' : 'badge-corporate'">
                            {{ shop.shiftPatternType === 2 ? 'シフト制' : '企業パターン' }}
                          </span>
                        </td>
                        <td></td>
                        <td>{{ shop.workDays }}日</td>
                        <td>{{ toHhmm(shop.workMinutes) }}</td>
                        <td>{{ toHhmm(shop.beyond8Minutes) }}</td>
                        <td>{{ toHhmm(shop.lateNightMinutes) }}</td>
                        <td>{{ yen(shop.hourlyWage) }}</td>
                        <td>{{ yen(shop.basePay) }}</td>
                        <td>{{ yen(shop.beyond8Pay) }}</td>
                        <td>{{ yen(shop.lateNightPay) }}</td>
                        <td>{{ yen(shop.transportationTotal) }}</td>
                        <td>{{ yen(shop.payrollTotal) }}</td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
              <p class="total-note">※支給額はシフトパターン店舗の分のみ。企業パターン店舗にも所属する場合、交通費は全店舗分を合計しますが支給合計には含まれません</p>
              <p class="total-note">※同じ日に複数店舗で勤務した場合、出勤日数・8時間超・交通費は店舗ごとに計上した値の合計です</p>
            </template>
          </template>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CompanySummaryPage',
  data() {
    const now = new Date();
    return {
      currentDate: new Date(now.getFullYear(), now.getMonth(), 1),
      loading: false,
      loadError: false,
      company: this.emptyCompany(),
      shops: [],
      expanded: {},
      // 'shop'（店舗別・既定） | 'staff'（スタッフ別）
      activeTab: 'shop',
      staffList: [],
      expandedStaff: {},
      // 集計は給与計算に使う帳票なので、初期状態は「含む」（従来の挙動）。
      // 他画面のトグルは初期 false だが、ここだけ true にしている。
      showArchived: true,
    };
  },
  watch: {
    showArchived() {
      this.getSummary();
    },
  },
  mounted() {
    this.getSummary();
  },
  methods: {
    emptyCompany() {
      return {
        shopCount: 0,
        staffCount: 0,
        archivedStaffCount: 0,
        workDays: 0,
        workMinutes: 0,
        beyond8Minutes: 0,
        lateNightMinutes: 0,
        basePay: null,
        beyond8Pay: null,
        lateNightPay: null,
        transportationTotal: 0,
        payrollTotal: null,
        payrollShopCount: 0,
      };
    },
    apiDate() {
      return this.currentDate.getFullYear() + '-' + (this.currentDate.getMonth() + 1) + '-01';
    },
    changeYear(delta) {
      this.currentDate = new Date(this.currentDate.getFullYear() + delta, this.currentDate.getMonth(), 1);
      this.getSummary();
    },
    selectMonth(monthIndex) {
      this.currentDate = new Date(this.currentDate.getFullYear(), monthIndex, 1);
      this.getSummary();
    },
    toggleShop(shopId) {
      this.expanded = { ...this.expanded, [shopId]: !this.expanded[shopId] };
    },
    toggleStaff(staffId) {
      this.expandedStaff = { ...this.expandedStaff, [staffId]: !this.expandedStaff[staffId] };
    },
    getSummary() {
      const companyId = this.$store.getters.company;
      this.loading = true;
      this.loadError = false;
      this.expanded = {};
      this.expandedStaff = {};
      this.$http.get(process.env.VUE_APP_API_PATH + '/company/summary/' + companyId + '/' + this.apiDate(), {
        // ON / OFF を常に明示送信する。他画面は「ON のときだけ送る」方式だが、
        // 集計はサーバ側の既定が「含む」なので、送らないと OFF にできない。
        params: { with_trashed: this.showArchived ? 1 : 0 }
      })
        .then(response => {
          const data = response.data.data;
          this.company = data.company;
          this.shops = data.shops;
          // バックエンド未デプロイでキーが undefined のときに描画を落とさない
          this.staffList = data.staff || [];
        })
        .catch(error => {
          console.log(error);
          this.loadError = true;
          this.company = this.emptyCompany();
          this.shops = [];
          this.staffList = [];
        })
        .then(() => {
          this.loading = false;
        });
    },
    excelDownload() {
      const companyId = this.$store.getters.company;
      const yearMonth = this.currentDate.getFullYear() + '-'
        + String(this.currentDate.getMonth() + 1).padStart(2, '0');

      this.$http.get(process.env.VUE_APP_API_PATH + '/company/summaryExcel/' + companyId + '/' + this.apiDate(), {
        params: { with_trashed: this.showArchived ? 1 : 0 },
        responseType: 'blob'
      })
        .then(response => {
          const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = '会社別集計_' + yearMonth + '.xlsx';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        })
        .catch(error => {
          console.log(error);
          this.$toast.error('Excel出力に失敗しました。時間をおいて再度お試しください。');
        });
    },
    // 分を "HH:MM" に整形する。24時間を超える合計も時間部分に繰り上げる。
    toHhmm(minutes) {
      if (minutes == null || minutes <= 0) {
        return '00:00';
      }
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
    },
    // 金額を桁区切り付きで表示する。null / undefined は「—」。0円とは表示しない。
    // != null にしているのは、バックエンド未デプロイでキーが undefined のときに
    // NaN円 を描画しないため。0 は通す。
    yen(amount) {
      return amount != null ? Number(amount).toLocaleString() + '円' : '—';
    },
  },
};
</script>

<style scoped>
.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}
.year-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.year-nav-btn {
  border: 1px solid #cbd5e0;
  background: #fff;
  border-radius: 4px;
  padding: 2px 10px;
  cursor: pointer;
}
.year-label {
  font-weight: 600;
  font-size: 16px;
}
.month-btn-group {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.month-btn {
  border: 1px solid #cbd5e0;
  background: #fff;
  border-radius: 4px;
  padding: 3px 10px;
  font-size: 13px;
  cursor: pointer;
}
.month-btn.active {
  background: #1a4731;
  border-color: #1a4731;
  color: #fff;
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}
.archive-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #4a5568;
  cursor: pointer;
  white-space: nowrap;
}
.archive-toggle input {
  width: 15px;
  height: 15px;
  cursor: pointer;
}
.summary-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
}
.summary-tab {
  border: 1px solid #cbd5e0;
  border-bottom: none;
  background: #fff;
  border-radius: 4px 4px 0 0;
  padding: 6px 18px;
  font-size: 14px;
  cursor: pointer;
}
.summary-tab.active {
  background: #1a4731;
  border-color: #1a4731;
  color: #fff;
}
.company-total {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 14px 16px;
  margin-bottom: 16px;
}
.company-total-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}
.total-item {
  display: flex;
  flex-direction: column;
}
.total-label {
  font-size: 11px;
  color: #718096;
}
.total-value {
  font-size: 18px;
  font-weight: 600;
}
.total-item-emphasis .total-value {
  color: #1a4731;
}
.total-note {
  font-size: 11px;
  color: #718096;
  margin: 8px 0 0;
}
.summary-table-wrap {
  overflow-x: auto;
}
.summary-table {
  min-width: 1100px;
  font-size: 13px;
}
.summary-table th {
  white-space: nowrap;
  text-align: right;
}
.summary-table td {
  text-align: right;
  white-space: nowrap;
}
.summary-table .th-name,
.summary-table .td-name {
  text-align: left;
}
/* 展開できる合計行（店舗別＝店舗行 / スタッフ別＝スタッフ行） */
.row-shop,
.row-staff-total {
  background: #edf2f7;
  font-weight: 600;
  cursor: pointer;
}
.row-shop:hover,
.row-staff-total:hover {
  background: #e2e8f0;
}
/* 展開して出る明細行（店舗別＝スタッフ行 / スタッフ別＝店舗内訳行） */
.row-staff td,
.row-breakdown td {
  color: #4a5568;
}
.td-staff-name,
.td-breakdown-name {
  padding-left: 32px;
}
.td-emphasis {
  color: #1a4731;
}
.badge-shift,
.badge-corporate {
  display: inline-block;
  font-size: 10px;
  font-weight: 500;
  border-radius: 3px;
  padding: 1px 6px;
  margin-left: 6px;
}
.badge-shift {
  background: #c6f6d5;
  color: #22543d;
}
.badge-corporate {
  background: #e2e8f0;
  color: #4a5568;
}
.total-sub {
  font-size: 11px;
  color: #718096;
}
.cell-sub {
  font-size: 11px;
  color: #718096;
}
.badge-archived {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  background: #fed7d7;
  color: #822727;
}
</style>
